import { useEffect, useState } from 'react';
import {
  Box,
  Image,
  Heading,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  HStack,
  Divider,
  Circle,
  VStack,
  IconButton,
  UnorderedList,
  ListItem,
  Center,
} from '@chakra-ui/react';
import { Link, NavLink } from 'react-router-dom';
import { useAbility } from '@casl/react';

import { ReactComponent as Logout } from '../../assets/sidebar/logout.svg';

import { useAppDispatch, useTypedSelector } from '../../hooks';
import { useNavigate } from 'react-router-dom';

import { logout } from '../../modules/auth';
import { sidebarContent } from './sidebar-content';
import { AbilityContext } from '../../providers';
import { apiSlice } from '../../services';
import { extractName } from '../../utils';

function Sidebar() {
  const [sidebarIndex, setSidebarIndex] = useState<number>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { userDetails } = useTypedSelector(state => state.auth);
  const ability = useAbility(AbilityContext);

  const allowedContent = sidebarContent.filter(
    ({ endpoint, hasNoGuard }) =>
      ability.can('GET', String(endpoint)) || hasNoGuard === true
  );

  useEffect(() => {
    if (location.pathname === '/') return setSidebarIndex(0);

    allowedContent.map(({ link }, contentIndex) => {
      if (link && location.pathname.startsWith('/' + link.split('/')[0])) {
        setSidebarIndex(contentIndex);
      }
    });
  }, [allowedContent.length, location.pathname]);

  return (
    <Box mx="6">
      <Link to="/">
        <Heading color="custom_blue.1" fontSize="3xl" h="10vh">
          <Box as="span" color="custom_orange.2">
            Brick
          </Box>
          Lynk
        </Heading>
      </Link>

      <Accordion
        className="hidden-scroll"
        h="calc(100vh - 33vh)"
        display="flex"
        flexDirection="column"
        index={sidebarIndex}
      >
        {allowedContent.map((content, contentIndex) => (
          <AccordionItem
            key={content.name}
            border="none"
            borderRadius="md"
            mt={content.name === 'Settings' ? 'auto' : '0'}
          >
            {({ isExpanded }) => {
              return (
                <>
                  <h2>
                    <AccordionButton
                      color="custom_blue.1"
                      _expanded={{
                        bg: 'custom_orange.2',
                        color: 'white',
                      }}
                      onClick={() => navigate('/' + content.link)}
                      borderRadius="md"
                    >
                      <HStack flex="1" gap="3">
                        {content.icon && (
                          <Image
                            src={`/sidebar/${
                              content.icon + (isExpanded ? '-selected' : '')
                            }.svg`}
                            alt={content.name + 'icon'}
                          />
                        )}
                        <Box fontWeight="medium">{content.name}</Box>
                      </HStack>
                      {Array.isArray(content.nestedLink) && <AccordionIcon />}
                    </AccordionButton>
                  </h2>

                  {content.nestedLink && content.nestedLink.length && (
                    <AccordionPanel pb={4}>
                      {content.nestedLink.map(({ name, link }) => (
                        <UnorderedList key={name}>
                          <ListItem>
                            <Box color="custom_blue.1" mb="1">
                              <NavLink to={`/${link}`}>
                                <Box px="2" py="1">
                                  {name}
                                </Box>
                              </NavLink>
                            </Box>
                          </ListItem>
                        </UnorderedList>
                      ))}
                    </AccordionPanel>
                  )}
                </>
              );
            }}
          </AccordionItem>
        ))}
      </Accordion>

      <Divider bg="custom_gray.4" />

      <HStack h="20vh">
        <Circle size="10" bg="custom_blue.1">
          {userDetails?.profilePictureThumbnailURL ? (
            <Image
              src={userDetails.profilePictureThumbnailURL}
              borderRadius="full"
            />
          ) : (
            <Center h="full" color="white" textTransform="capitalize">
              {extractName(
                `${userDetails?.firstName} ${userDetails?.lastName}`
              )}
            </Center>
          )}
        </Circle>

        <VStack align="start" maxW="32">
          <Box
            color="custom_blue.1"
            fontSize="md"
            fontWeight="semibold"
            textOverflow="ellipsis"
            overflow="hidden"
            whiteSpace="nowrap"
            maxW="32"
          >
            {userDetails?.firstName}
          </Box>
          <Box
            color="custom_blue.1"
            fontSize="sm"
            textOverflow="ellipsis"
            overflow="hidden"
            whiteSpace="nowrap"
            maxW="32"
          >
            {userDetails?.email}
          </Box>
        </VStack>
        <IconButton
          onClick={() => {
            dispatch(logout());
            dispatch(apiSlice.util.resetApiState());
            ['accessToken', 'refreshToken'].forEach(key =>
              localStorage.removeItem(key)
            );
          }}
          aria-label="logout"
          bg="none"
          _hover={{ bg: 'none' }}
          icon={<Logout />}
        />
      </HStack>
    </Box>
  );
}

export default Sidebar;
